import { Gauge, Grid3x3, DollarSign, ClipboardCheck, Timer, ClipboardList } from "lucide-react";

export const TOOLS = [
  { key: "oee", slug: "oee-calculator", title: "OEE Calculator", icon: Gauge, minutes: "2 min",
    desc: "Availability × Performance × Quality, benchmarked against world-class 85%." },
  { key: "fives", slug: "5s-audit", title: "5S Audit Scorer", icon: Grid3x3, minutes: "10 min",
    desc: "25-question audit across Sort, Set, Shine, Standardize, Sustain with a radar chart." },
  { key: "downtime", slug: "downtime-cost", title: "Downtime Cost Calculator", icon: DollarSign, minutes: "3 min",
    desc: "What every hour of unplanned downtime really costs, per hour and per year." },
  { key: "capa", slug: "capa-builder", title: "CAPA Builder", icon: ClipboardCheck, minutes: "20 min",
    desc: "Guided 8D + 5-Why corrective action report you can print and file." },
  { key: "cycle", slug: "cycle-time-analyzer", title: "Cycle Time Analyzer", icon: Timer, minutes: "5 min",
    desc: "Stations vs. takt time, bottleneck detection, and line balance efficiency." },
  { key: "handover", slug: "shift-handover", title: "Shift Handover Log", icon: ClipboardList, minutes: "5 min",
    desc: "Structured, printable shift handover: safety, output, quality, equipment, actions." },
];

const KEY = "ruleai_tool_results";
export const RESULTS_EVENT = "ruleai-tools-updated";

export const loadResults = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
};

export const saveResult = (key, data) => {
  const r = loadResults();
  r[key] = { ...data, at: new Date().toISOString() };
  localStorage.setItem(KEY, JSON.stringify(r));
  window.dispatchEvent(new Event(RESULTS_EVENT));
};

export const loadDraft = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(`ruleai_draft_${key}`)) || fallback; } catch { return fallback; }
};
export const saveDraft = (key, data) => localStorage.setItem(`ruleai_draft_${key}`, JSON.stringify(data));

export const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const pct = (v, d = 1) => `${(v * 100).toFixed(d)}%`;
export const money = (v) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e4) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};

// Health score: OEE vs 85% world-class, 5S %, downtime availability loss (25% loss => 0)
export const healthScore = (r) => {
  const parts = [];
  if (r.oee) parts.push({ key: "oee", label: "OEE", value: Math.min(100, (r.oee.score / 85) * 100), raw: `${r.oee.score.toFixed(1)}%` });
  if (r.fives) parts.push({ key: "fives", label: "5S", value: r.fives.score, raw: `${Math.round(r.fives.score)}/100` });
  if (r.downtime) parts.push({ key: "downtime", label: "Downtime", value: Math.max(0, 100 - r.downtime.lossPct * 4), raw: `${r.downtime.lossPct.toFixed(1)}% lost` });
  const score = parts.length ? Math.round(parts.reduce((s, p) => s + p.value, 0) / parts.length) : null;
  return { score, parts };
};

export const tierFor = (s) => (s >= 85 ? { label: "World-class", tone: "text-emerald-400" }
  : s >= 60 ? { label: "Typical", tone: "text-blue-400" }
  : s >= 40 ? { label: "Below average", tone: "text-amber-400" }
  : { label: "Needs attention", tone: "text-red-400" });

// Draws a 1200x627 (LinkedIn-ready) branded share card and returns a PNG data URL
export async function drawShareCard({ tool, scoreText, label, bar, tagline }) {
  if (document.fonts?.ready) await document.fonts.ready;
  const c = document.createElement("canvas");
  c.width = 1200; c.height = 627;
  const g = c.getContext("2d");
  g.fillStyle = "#0a0a0b"; g.fillRect(0, 0, 1200, 627);
  g.strokeStyle = "rgba(255,255,255,0.045)"; g.lineWidth = 1;
  for (let x = 0; x <= 1200; x += 48) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, 627); g.stroke(); }
  for (let y = 0; y <= 627; y += 48) { g.beginPath(); g.moveTo(0, y); g.lineTo(1200, y); g.stroke(); }
  g.fillStyle = "#ea580c"; g.fillRect(0, 0, 14, 627);
  g.fillStyle = "#60a5fa"; g.font = "600 24px 'IBM Plex Mono', monospace";
  g.fillText(`◉ ${tool.toUpperCase()}`, 80, 96);
  let size = 190;
  g.font = `900 ${size}px Chivo, sans-serif`;
  while (g.measureText(scoreText).width > 1040 && size > 80) { size -= 10; g.font = `900 ${size}px Chivo, sans-serif`; }
  g.fillStyle = "#fafafa"; g.fillText(scoreText, 72, 300);
  g.fillStyle = "#a1a1aa"; g.font = "500 34px Inter, sans-serif"; g.fillText(label, 80, 360);
  if (bar) {
    const x = 80, y = 400, w = 720, h = 16;
    g.fillStyle = "#27272a"; g.fillRect(x, y, w, h);
    g.fillStyle = "#3b82f6"; g.fillRect(x, y, w * Math.min(1, Math.max(0, bar.value / 100)), h);
    const tx = x + w * Math.min(1, bar.target / 100);
    g.fillStyle = "#ea580c"; g.fillRect(tx - 2, y - 12, 4, h + 24);
    g.font = "500 20px 'IBM Plex Mono', monospace"; g.fillText(bar.targetLabel, Math.min(tx - 10, x + w - 220), y + 58);
  }
  g.fillStyle = "#fafafa"; g.font = "700 32px Inter, sans-serif"; g.fillText(tagline, 80, 530);
  g.font = "900 40px Chivo, sans-serif";
  const parts = [["Rule", "#fafafa"], ["AI", "#60a5fa"], [".systems", "#71717a"]];
  let bx = 1120 - parts.reduce((s, [t]) => s + g.measureText(t).width, 0);
  parts.forEach(([t, col]) => { g.fillStyle = col; g.fillText(t, bx, 100); bx += g.measureText(t).width; });
  g.fillStyle = "#71717a"; g.font = "500 20px 'IBM Plex Mono', monospace";
  g.textAlign = "right"; g.fillText("Free tools · ruleai.systems/tools", 1120, 580);
  return c.toDataURL("image/png");
}
